import { AiChatComponent } from '../ai-chat/ai-chat';
import { Component, CUSTOM_ELEMENTS_SCHEMA, inject, signal, HostListener } from '@angular/core';
import { RouterOutlet, Router, RouterModule, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { filter } from 'rxjs/operators';
import { Header } from '../header/header';
import { Footer } from '../footer/footer';
import { Notification } from '../notification/notification';
import { LoadingSpinner } from '../loading-spinner/loading-spinner';

interface NavItem {
  label: string;
  route: string;
  iconPath: string;
  exact?: boolean;
}

@Component({
  selector: 'app-shell-layout',
  standalone: true,
  imports: [
    AiChatComponent,RouterOutlet, CommonModule, RouterModule, Header, Footer, Notification, LoadingSpinner],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './shell-layout.html',
  styleUrl: './shell-layout.scss',
})
export class ShellLayout {
  private router = inject(Router);

  sidebarCollapsed = signal<boolean>(true);
  currentRoute = signal<string>('');

  readonly navItems: NavItem[] = [
    {
      label: 'Home',
      route: '/',
      exact: true,
      iconPath: 'M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z',
    },
    {
      label: 'Monthly Reports',
      route: '/monthly-reports',
      iconPath:
        'M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z',
    },
    {
      label: 'Billing Analysis',
      route: '/billing',
      iconPath:
        'M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z',
    },
    {
      label: 'PO Processing',
      route: '/po-processing',
      iconPath:
        'M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm-2 14l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z',
    },
    {
      label: 'Master Data',
      route: '/master-data',
      iconPath: 'M4 6h16v2H4zm0 5h16v2H4zm0 5h16v2H4z',
    },
    {
      label: 'Dashboard',
      route: '/dashboard',
      iconPath: 'M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z',
    },
    {
      label: 'Financial Forecast',
      route: '/forecast',
      iconPath: 'M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z',
    },
    {
      label: 'Lease Admin',
      route: '/lb-reports',
      iconPath: 'M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z',
    },
  ];

  constructor() {
    // Track active route for sidebar highlight
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.currentRoute.set(e.urlAfterRedirects));

    this.currentRoute.set(this.router.url);
  }

  isActive(item: NavItem): boolean {
    const url = this.currentRoute();
    if (item.exact) return url === item.route || url === '';
    return url.startsWith(item.route);
  }

  navigate(route: string): void {
    this.router.navigate([route]);
  }

  toggleSidebar(): void {
    this.sidebarCollapsed.update((v) => !v);
  }

  /** Ctrl+\ to toggle sidebar from anywhere */
  @HostListener('document:keydown', ['$event'])
  onGlobalKey(e: KeyboardEvent): void {
    if (e.ctrlKey && e.key === '\\') {
      e.preventDefault();
      this.toggleSidebar();
    }
  }
}